'use client'

import React from 'react'

import { IS_PRODUCTION } from '~/constants/system'
import { SERVER_OVERRIDE_KEY } from '~/utils/serverOrigin'

const PR_PATTERN = /^pr-(\d+)$/

/**
 * Reads the `api` query parameter on mount and persists a server override
 * to localStorage. Also renders a small fixed banner when an override is active
 * so the user knows they are pointed at a non-default backend.
 *
 * Disabled in production.
 */
export function ServerOverrideBanner() {
  const [override, setOverride] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (IS_PRODUCTION) return

    // Handle ?api= query parameter
    const params = new URLSearchParams(window.location.search)
    const api = params.get('api')

    if (api === 'reset') {
      localStorage.removeItem(SERVER_OVERRIDE_KEY)
      // Clean the query param from the URL, then reload so all components
      // pick up the cleared override (avoids race with React render)
      params.delete('api')
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '') +
        window.location.hash
      window.history.replaceState({}, '', newUrl)
      window.location.reload()
      return
    }

    if (api && PR_PATTERN.test(api)) {
      const url = `https://${api}.server.testing.splits.org`
      localStorage.setItem(SERVER_OVERRIDE_KEY, url)
      // Remove the query param from the URL without reloading
      params.delete('api')
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '') +
        window.location.hash
      window.history.replaceState({}, '', newUrl)
      setOverride(api)
      return
    }

    // Read existing override from localStorage
    const existing = localStorage.getItem(SERVER_OVERRIDE_KEY)
    if (existing) {
      const match = existing.match(
        /^https:\/\/(pr-\d+)\.server\.testing\.splits\.org$/,
      )
      setOverride(match ? match[1] : existing)
    }
  }, [])

  if (IS_PRODUCTION) return null
  if (!override) return null

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 6,
        fontSize: 12,
        fontFamily: 'monospace',
        color: '#fff',
        backgroundColor: 'rgba(139, 92, 246, 0.85)',
        backdropFilter: 'blur(4px)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}
    >
      <span>API: {override}</span>
      <button
        onClick={() => {
          localStorage.removeItem(SERVER_OVERRIDE_KEY)
          window.location.reload()
        }}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          borderRadius: 4,
          color: '#fff',
          cursor: 'pointer',
          fontSize: 11,
          padding: '2px 6px',
        }}
      >
        ✕
      </button>
    </div>
  )
}
